import React from "react";
import { MessageCircle } from "lucide-react";

export default function AdminCustomOrders({
  customOrder,
  setCustomOrder,
  manualItems,
  updateManualItem,
  addManualItem,
  removeManualItem,
  manualSubtotal,
  manualGrandTotal,
  addCustomOrder,
  orders,
  manualOrders,
  totalSales,
  stats,
  setOrderStatus,
  orderStatuses,
  waLink,
}) {
  return (
    <div className="mt-6 space-y-6" data-testid="admin-custom-orders">
          <div>
            <div className="overline">Manual Order Entry</div>
            <h2 className="display-h3 mt-2">Add a customer order</h2>
            <p className="text-sm text-stone-600 mt-1">
              Add multiple different products to one purchase. Everything is saved as one order number,
              so the customer can track the complete purchase.
            </p>
          </div>

          <form onSubmit={addCustomOrder} className="space-y-5">
            <div className="card-pmt p-5">
              <div className="overline">Customer Details</div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="label-pmt">Customer Name *</div>
                  <input required className="input-pmt" value={customOrder.name}
                    onChange={(e) => setCustomOrder({ ...customOrder, name: e.target.value })} />
                </div>
                <div>
                  <div className="label-pmt">Phone *</div>
                  <input required className="input-pmt" value={customOrder.phone}
                    onChange={(e) => setCustomOrder({ ...customOrder, phone: e.target.value })} />
                </div>
                <div>
                  <div className="label-pmt">Email</div>
                  <input type="email" className="input-pmt" value={customOrder.email}
                    onChange={(e) => setCustomOrder({ ...customOrder, email: e.target.value })} />
                </div>
                <div className="md:col-span-3">
                  <div className="label-pmt">Delivery Address *</div>
                  <input required className="input-pmt" value={customOrder.address}
                    onChange={(e) => setCustomOrder({ ...customOrder, address: e.target.value })}
                    placeholder="Customer delivery address" />
                </div>
                <div>
                  <div className="label-pmt">City *</div>
                  <input required className="input-pmt" value={customOrder.city}
                    onChange={(e) => setCustomOrder({ ...customOrder, city: e.target.value })} />
                </div>
                <div>
                  <div className="label-pmt">District *</div>
                  <input required className="input-pmt" value={customOrder.district}
                    onChange={(e) => setCustomOrder({ ...customOrder, district: e.target.value })} />
                </div>
                <div>
                  <div className="label-pmt">State</div>
                  <input className="input-pmt" value={customOrder.state}
                    onChange={(e) => setCustomOrder({ ...customOrder, state: e.target.value })} />
                </div>
                <div>
                  <div className="label-pmt">Pincode *</div>
                  <input required className="input-pmt" value={customOrder.pincode}
                    onChange={(e) => setCustomOrder({ ...customOrder, pincode: e.target.value })} />
                </div>
                <div>
                  <div className="label-pmt">Order Received Via</div>
                  <select className="input-pmt" value={customOrder.source}
                    onChange={(e) => setCustomOrder({ ...customOrder, source: e.target.value })}>
                    <option value="social_media">Social Media</option>
                    <option value="phone">Phone Call</option>
                    <option value="walk-in">Walk-in</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="overline">Products in this purchase</div>
                  <h3 className="font-bold mt-1">Add different products to the same order</h3>
                </div>
                <button type="button" onClick={addManualItem} className="btn-outline-pmt">
                  + Add Another Product
                </button>
              </div>

              {manualItems.map((item, index) => (
                <div key={index} className="card-pmt p-5">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="font-bold">Product {index + 1}</div>
                    {manualItems.length > 1 && (
                      <button type="button" onClick={() => removeManualItem(index)}
                        className="text-sm text-red-600 underline">
                        Remove Product
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="label-pmt">Product *</div>
                      <select className="input-pmt" value={item.product_type}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateManualItem(index, "product_type", value);
                          updateManualItem(index, "product_name", value);
                        }}>
                        {["Custom T-Shirts","Oversized T-Shirts","Hoodies","Sports Jerseys","Corporate T-Shirts","College T-Shirts","Couple T-Shirts","Family T-Shirts","Bulk Order"].map((p) => (
                          <option key={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="label-pmt">Product Name</div>
                      <input className="input-pmt" value={item.product_name}
                        onChange={(e) => updateManualItem(index, "product_name", e.target.value)}
                        placeholder="e.g. Black Oversized T-Shirt" />
                    </div>
                    <div>
                      <div className="label-pmt">Quantity *</div>
                      <input required type="number" min="1" className="input-pmt" value={item.quantity}
                        onChange={(e) => updateManualItem(index, "quantity", e.target.value)} />
                    </div>
                    <div>
                      <div className="label-pmt">Unit Price (₹) *</div>
                      <input required type="number" min="1" step="0.01" className="input-pmt" value={item.unit_price}
                        onChange={(e) => updateManualItem(index, "unit_price", e.target.value)}
                        placeholder="Agreed price per item" />
                    </div>
                    <div>
                      <div className="label-pmt">Size / Sizes</div>
                      <input className="input-pmt" value={item.size} placeholder="M, L, XL or 2 M + 1 L"
                        onChange={(e) => updateManualItem(index, "size", e.target.value)} />
                    </div>
                    <div>
                      <div className="label-pmt">Colour</div>
                      <input className="input-pmt" value={item.color}
                        onChange={(e) => updateManualItem(index, "color", e.target.value)} />
                    </div>
                    <div>
                      <div className="label-pmt">Quality / GSM</div>
                      <input className="input-pmt" value={item.quality}
                        onChange={(e) => updateManualItem(index, "quality", e.target.value)}
                        placeholder="180 GSM / 240 GSM" />
                    </div>
                    <div>
                      <div className="label-pmt">Printing</div>
                      <select className="input-pmt" value={item.printing_method}
                        onChange={(e) => updateManualItem(index, "printing_method", e.target.value)}>
                        {["DTF","Vinyl","Sublimation","Not sure — recommend for me"].map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="label-pmt">Design ID</div>
                      <input className="input-pmt" value={item.design_id}
                        onChange={(e) => updateManualItem(index, "design_id", e.target.value)}
                        placeholder="Optional PMT-001" />
                    </div>
                    <div>
                      <div className="label-pmt">T-Shirt Style</div>
                      <input className="input-pmt" value={item.tshirt_style}
                        onChange={(e) => updateManualItem(index, "tshirt_style", e.target.value)}
                        placeholder="Round neck / Collar / Oversized" />
                    </div>
                    <div>
                      <div className="label-pmt">Customer Name on T-shirt</div>
                      <input className="input-pmt" value={item.custom_name}
                        onChange={(e) => updateManualItem(index, "custom_name", e.target.value)} />
                    </div>
                    <div>
                      <div className="label-pmt">Number</div>
                      <input className="input-pmt" value={item.custom_number}
                        onChange={(e) => updateManualItem(index, "custom_number", e.target.value)} />
                    </div>
                    <div className="md:col-span-3">
                      <div className="label-pmt">Custom Text / Artwork Notes</div>
                      <textarea rows={2} className="input-pmt" value={item.custom_text}
                        onChange={(e) => updateManualItem(index, "custom_text", e.target.value)}
                        placeholder="Logo placement, text, artwork details, customer requirements..." />
                    </div>
                    <div className="md:col-span-3">
                      <div className="label-pmt">Gift Personalisation (optional)</div>
                      <textarea rows={3} className="input-pmt" value={item.gift_options_text || ""}
                        onChange={(e) => updateManualItem(index, "gift_options_text", e.target.value)}
                        placeholder={"Recipient name: Asha\nGift message: Happy birthday"} />
                      <p className="mt-1 text-xs text-stone-500">Enter one field per line as Label: value. Up to 20 fields.</p>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end border-t border-[#E6DFD5] pt-4">
                    <div className="text-right">
                      <div className="text-xs text-stone-500">Product Total</div>
                      <div className="text-xl font-bold">
                        ₹{(Number(item.quantity || 0) * Number(item.unit_price || 0)).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card-pmt p-5">
              <div className="overline">Payment & Total</div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="label-pmt">Payment Status</div>
                  <select className="input-pmt" value={customOrder.payment_status}
                    onChange={(e) => setCustomOrder({ ...customOrder, payment_status: e.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="partial">Partially Paid</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
                <div>
                  <div className="label-pmt">Payment Method</div>
                  <select className="input-pmt" value={customOrder.payment_method}
                    onChange={(e) => setCustomOrder({ ...customOrder, payment_method: e.target.value })}>
                    <option value="pending">Not Paid</option>
                    <option value="UPI">UPI</option>
                    <option value="Cash">Cash</option>
                    <option value="Cash on Delivery">Cash on Delivery</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Credit/Debit Card">Credit/Debit Card</option>
                  </select>
                </div>
                <div>
                  <div className="label-pmt">Shipping Charge (₹)</div>
                  <input type="number" min="0" step="0.01" className="input-pmt" value={customOrder.shipping_charge}
                    onChange={(e) => setCustomOrder({ ...customOrder, shipping_charge: e.target.value })} />
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-[#E6DFD5] pt-4">
                <div>
                  <div className="text-xs text-stone-500">Products</div>
                  <div className="text-lg font-bold">{manualItems.length}</div>
                </div>
                <div>
                  <div className="text-xs text-stone-500">Subtotal</div>
                  <div className="text-lg font-bold">₹{manualSubtotal.toLocaleString("en-IN")}</div>
                </div>
                <div>
                  <div className="text-xs text-stone-500">Order Total</div>
                  <div className="text-2xl font-bold">₹{manualGrandTotal.toLocaleString("en-IN")}</div>
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <button type="submit" className="btn-primary">
                  Create Order & Generate Order Number
                </button>
              </div>
            </div>
          </form>

          <div className="card-pmt p-5">
            <div className="overline">Sales Summary</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><div className="text-xs text-stone-500">Total Orders</div><div className="text-2xl font-bold">{orders.length}</div></div>
              <div><div className="text-xs text-stone-500">Manual Orders</div><div className="text-2xl font-bold">{manualOrders.length}</div></div>
              <div><div className="text-xs text-stone-500">Total Sales</div><div className="text-2xl font-bold">₹{totalSales.toLocaleString("en-IN")}</div></div>
              <div><div className="text-xs text-stone-500">Paid Orders</div><div className="text-2xl font-bold">{stats.paid}</div></div>
            </div>
          </div>

          <div>
            <div className="overline">Recent Manual Orders</div>
            <div className="mt-3 overflow-auto border border-[#E6DFD5]">
              <table className="min-w-[1200px] w-full text-sm">
                <thead className="text-xs uppercase text-stone-500 bg-[#F3EDE2]">
                  <tr>
                    <th className="text-left py-3 px-3">Order #</th>
                    <th className="text-left px-3">Customer</th>
                    <th className="text-left px-3">Products</th>
                    <th className="text-left px-3">Qty</th>
                    <th className="text-left px-3">Sales</th>
                    <th className="text-left px-3">Payment</th>
                    <th className="text-left px-3">Status</th>
                    <th className="text-left px-3">Track</th>
                  </tr>
                </thead>
                <tbody>
                  {manualOrders.map((o) => (
                    <tr key={o.order_number} className="border-t border-[#E6DFD5]">
                      <td className="py-3 px-3 font-mono font-bold">{o.order_number}</td>
                      <td className="px-3">{o.full_name}<div className="text-xs text-stone-500">{o.mobile}</div></td>
                      <td className="px-3">
                        {(o.items || []).map((item, i) => (
                          <div key={i} className="mb-1">
                            <div>{item.product_name} × {item.quantity}</div>
                            {item.gift_options && Object.entries(item.gift_options)
                              .filter(([, value]) => typeof value === "string" && value.trim())
                              .map(([key, value]) => (
                                <div key={key} className="text-xs text-stone-500">
                                  {(item.gift_option_labels || {})[key] || key.replace(/_/g, " ")}: {value}
                                </div>
                              ))}
                          </div>
                        ))}
                      </td>
                      <td className="px-3">{(o.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0)}</td>
                      <td className="px-3 font-bold">₹{Number(o.total_amount || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3">{String(o.payment_status || "pending").toUpperCase()}</td>
                      <td className="px-3">
                        <select className="input-pmt text-xs py-1" value={o.status}
                          onChange={(e) => setOrderStatus(o.order_number, e.target.value)}>
                          {orderStatuses.map((status) => <option key={status}>{status}</option>)}
                        </select>
                      </td>
                      <td className="px-3">
                        <a href={waLink(`Hi ${o.full_name}, your PrintMyTees order number is ${o.order_number}. You can track the complete purchase at printmytees.in/track-order.`)}
                          target="_blank" rel="noreferrer" className="btn-whatsapp text-xs whitespace-nowrap">
                          <MessageCircle size={12} /> Send Order #
                        </a>
                      </td>
                    </tr>
                  ))}
                  {manualOrders.length === 0 && (
                    <tr><td colSpan={8} className="py-10 text-center text-stone-500">No manual orders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
      </div>
  );
}
